<template>
  <div class="flex justify-center">
    <InvoiceActionsMenu
      :invoice="invoice"
      :tippy-options="tippyOptions"
      :visible-actions="visibleActions"
      :tenant-spb-enabled="tenantSpbEnabled"
      @invoice-action="handleInvoiceAction"
    >
      <template #trigger>
        <slot name="trigger">
          <SeraButton
            v-if="hasButtonMenu"
            text="Invoice Actions"
            icon="fa-regular fa-chevron-down"
            icon-position="right"
            data-cy="invoice-actions"
          />
        </slot>
      </template>
      <template #footer>
        <div
          v-if="allowToggleDisplayMembershipSavings"
          class="border-t-[1px] border-gray-300"
        >
          <div
            class="flex justify-between align-center whitespace-nowrap px-5 py-4"
          >
            <span class="!text-gray-700 font-medium text-[12px]">
              Show Membership Savings
            </span>
            <ToggleSwitch
              v-model="displayMembershipSavings"
              data-cy="display-membership-savings-toggle"
              @update:modelValue="handleToggleMembershipSavings"
            />
          </div>
        </div>
      </template>
    </InvoiceActionsMenu>
    <AcceptPaymentV2
      v-if="paymentModal && newPaymentModalsEnabled"
      :invoice="invoice"
      :disabled="acceptPaymentDisabled"
      @hide-modal="paymentModal = false"
    />
    <AcceptPayment
      v-if="paymentModal && !newPaymentModalsEnabled"
      :invoice="invoice"
      :disabled="invoice.financials.balance_cents <= 0"
      @hide-modal="paymentModal = false"
    />
    <WorkCompleteModal
      v-if="signatureModal"
      :invoice="invoice"
      @hide-modal="signatureModal = false"
    />
    <RefundModalV2
      v-if="refundModal && newPaymentModalsEnabled"
      :invoice="invoice"
      @hide-modal="refundModal = false"
    />
    <RefundModal
      v-if="refundModal && !newPaymentModalsEnabled"
      :invoice="invoice"
      @hide-modal="refundModal = false"
    />
    <ActionConfirmationModal
      v-if="resubmitModal"
      :modal-contents="resubmitModalContents"
      @hide-modal="resubmitModal = false"
    />
    <EditInvoiceDateModal
      v-if="editDateModal"
      :invoice="invoice"
      @hide-modal="editDateModal = false"
    />
    <EmailQuoteOrInvoiceModal
      v-if="showEmailInvoiceModal"
      :invoice="invoice"
      @hide-modal="showEmailInvoiceModal = false"
    />
    <VoidInvoiceModal
      v-if="voidModal"
      :invoice="invoice"
      @hide-modal="voidModal = false"
    />
    <UnapproveInvoiceModal
      v-if="unapproveModal"
      :invoice="invoice"
      @hide-modal="unapproveModal = false"
    />
    <InvoiceDetailsDrawer
      v-if="showInvoiceDetailsDrawer"
      :invoice="invoice"
      @hide-drawer="showInvoiceDetailsDrawer = false"
    />
    <ConfigurePaymentScheduleModal
      v-if="scheduleModal && invoice.job_id"
      :job-id="invoice.job_id"
      :invoice-balance-cents="scheduleModalBalanceCents"
      :existing-plan="scheduleModalPlan"
      :plan-locked="scheduleModalLocked"
      @hide-modal="scheduleModal = false"
      @saved="handleScheduleSaved"
      @needs-refresh="handleScheduleSaved"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { EventBus } from "@/lib/event_bus";
import { useInvoiceActions } from "@/lib/useInvoiceActions";
import { useJobWarnings } from "@/lib/useJobWarnings";

import SeraButton from "@/components/Button.vue";
import InvoiceActionsMenu from "@/components/Invoice/InvoiceActionsMenu.vue";
import InvoiceDetailsDrawer from "@/components/Invoice/InvoiceDetailsDrawer.vue";
import ToggleSwitch from "@/components/ToggleSwitch.vue";
import AcceptPayment from "@/AcceptPayment/AcceptPayment.vue";
import AcceptPaymentV2 from "@/AcceptPayment/AcceptPaymentV2.vue";
import EmailQuoteOrInvoiceModal from "@/EmailQuoteOrInvoice/EmailQuoteOrInvoiceModal.vue";
import EditInvoiceDateModal from "@/Invoice/EditInvoiceDateModal.vue";
import RefundModal from "@/Invoice/RefundModal.vue";
import RefundModalV2 from "@/Invoice/RefundModalV2.vue";
import UnapproveInvoiceModal from "@/Invoice/UnapproveInvoiceModal.vue";
import VoidInvoiceModal from "@/Invoice/VoidInvoiceModal.vue";
import WorkCompleteModal from "@/Invoice/WorkCompleteModal.vue";
import ConfigurePaymentScheduleModal from "@/Job/QuoteBuilder/ConfigurePaymentScheduleModal.vue";
import ActionConfirmationModal from "@/Reports/ActionConfirmationModal.vue";
import type { Invoice } from "@/typings/invoice";
import type { TippyOptions } from "@/typings/ui";
import { InvoiceAction } from "@/typings/ui";

const props = withDefaults(
  defineProps<{
    invoice: Invoice;
    tippyOptions?: TippyOptions;
    visibleActions?: InvoiceAction[];
    hasButtonMenu?: boolean;
    hideMembershipSavingsToggle?: boolean;
  }>(),
  {
    tippyOptions: () => ({}),
    visibleActions: undefined,
    hasButtonMenu: false,
    hideMembershipSavingsToggle: false
  }
);

const store = useStore();
const invoiceActions = useInvoiceActions();
const { withJobWarnings } = useJobWarnings();

const paymentModal = ref(false);
const signatureModal = ref(false);
const refundModal = ref(false);
const resubmitModal = ref(false);
const editDateModal = ref(false);
const showEmailInvoiceModal = ref(false);
const unapproveModal = ref(false);
const voidModal = ref(false);
const showInvoiceDetailsDrawer = ref(false);
const scheduleModal = ref(false);
const scheduleModalPlan = ref<any>(null);
const scheduleModalLocked = ref(false);
const scheduleModalBalanceCents = ref(0);
const displayMembershipSavings = ref(props.invoice.display_membership_savings);

const tenant = computed(() => store.state.tenant);

const newPaymentModalsEnabled = computed(() =>
  store.getters.featureEnabledForTenant("new_payment_modals")
);

// Tenant-level "Progress Billing enabled" toggle (set in the
// ProgressBilling settings page). Lazy-fetched on mount so the actions
// menu can decide whether to expose "Configure Payment Schedule" on
// invoices that don't yet have a plan. The fetch only runs when the
// feature flag is on for the tenant; otherwise the menu item stays
// hidden regardless.
const tenantSpbEnabled = ref(false);
const progressBillingFFEnabled = computed(() =>
  store.getters.featureEnabledForTenant("progress_billing")
);

const acceptPaymentDisabled = computed(() => {
  const spb = props.invoice.progress_billing;
  if (spb?.is_progress_billing) {
    return (spb.total_balance_remaining_cents ?? 0) <= 0;
  }
  return (props.invoice.financials?.balance_cents ?? 0) <= 0;
});

onMounted(async () => {
  if (!progressBillingFFEnabled.value) return;
  try {
    const config = await store.dispatch("fetchProgressBillingConfig");
    tenantSpbEnabled.value = Boolean(config?.enabled);
  } catch {
    tenantSpbEnabled.value = false;
  }
});

const allowToggleDisplayMembershipSavings = computed(() => {
  return (
    store.getters.featureEnabledForTenant(
      "toggle_display_membership_savings"
    ) &&
    tenant.value?.display_membership_savings_ad_hoc &&
    !props.hideMembershipSavingsToggle &&
    !props.invoice.finalized &&
    props.invoice.links?.update_display_membership_savings
  );
});

const resubmitModalContents = computed(() => {
  if (!props.invoice) return {};
  return {
    message:
      "Resubmitting this invoice for billing will attempt to collect payment with the payment method on file.",
    title: "Confirm Rebilling",
    success_message: `Invoice #${props.invoice.invoice_id} submitted for rebilling.  Please check the status in a few minutes.`,
    event_name: "invoice-resubmitted",
    action_url: props.invoice.links?.resubmit,
    hide_input: true
  };
});

async function handleApproveInvoice() {
  const hasFulfilledAllItems = !props.invoice.items_attributes.find(
    (item) => item.equipment_request_status === "unfulfilled"
  );
  if (hasFulfilledAllItems) {
    signatureModal.value = true;
    return;
  }
  await store.dispatch("confirmChanges", {
    title: "Equipment Requested",
    message:
      "There are items that require equipment to be captured.<br/> Are you sure you wish to continue?"
  });
  signatureModal.value = true;
}

async function handleInvoiceAction({
  action
}: {
  id: number;
  action: InvoiceAction;
}) {
  switch (action) {
    case InvoiceAction.Approve:
      await handleApproveInvoice();
      break;
    case InvoiceAction.AcceptPayment:
      paymentModal.value = true;
      break;
    case InvoiceAction.IssueRefund:
      refundModal.value = true;
      break;
    case InvoiceAction.EditDate:
      withJobWarnings({
        action: () => {
          editDateModal.value = true;
        },
        record: props.invoice,
        context: "edit-invoice"
      });
      break;
    case InvoiceAction.Unapprove:
      withJobWarnings({
        action: () => {
          unapproveModal.value = true;
        },
        record: props.invoice,
        context: "edit-invoice"
      });
      break;
    case InvoiceAction.Void:
      voidModal.value = true;
      break;
    case InvoiceAction.Resubmit:
      resubmitModal.value = true;
      break;
    case InvoiceAction.Email:
      showEmailInvoiceModal.value = true;
      break;
    case InvoiceAction.EditDetails:
      showInvoiceDetailsDrawer.value = true;
      break;
    // SPB schedule management. The actions menu is mounted in several
    // contexts (Job's Invoice tab, Customer's Invoices tab, Revenue
    // report) — handling these inline keeps the behaviour identical
    // wherever the menu appears, instead of relying on a sibling
    // component to listen.
    case InvoiceAction.EditPaymentSchedule:
      await openScheduleModal();
      break;
    case InvoiceAction.RemovePaymentSchedule:
      await removeSchedule();
      break;
    default:
      await invoiceActions.perform(props.invoice, action);
      break;
  }
}

// Mirrors the resolution InvoiceTab uses when opening this modal — the
// modal expects a "target total" that respects plan drift / needs-review
// so percentages don't rebase against a stale snapshot. The plan is
// fetched fresh just before the modal opens.
function resolveScheduleBalanceCents(plan: any): number {
  const inv = props.invoice;
  const liveBalance = inv?.financials?.balance_cents ?? 0;
  if (!plan) return liveBalance;
  if (plan.split_mode === "percentage") {
    if (inv?.progress_billing?.plan_needs_review) return liveBalance;
    return plan.target_total_cents > 0 ? plan.target_total_cents : liveBalance;
  }
  if (inv?.progress_billing?.plan_drift) return liveBalance;
  return plan.target_total_cents > 0 ? plan.target_total_cents : liveBalance;
}

async function openScheduleModal() {
  if (!props.invoice.job_id) return;
  try {
    const response = await store.dispatch(
      "fetchJobPaymentSchedule",
      props.invoice.job_id
    );
    scheduleModalPlan.value = response?.schedule ?? null;
  } catch {
    scheduleModalPlan.value = null;
  }
  scheduleModalBalanceCents.value = resolveScheduleBalanceCents(
    scheduleModalPlan.value
  );
  scheduleModalLocked.value = Boolean(props.invoice.finalized);
  scheduleModal.value = true;
}

function handleScheduleSaved() {
  scheduleModal.value = false;
  // Reuse the existing refresh broadcast so InvoiceTab (and any other
  // listener) re-fetches the job invoice and the local schedule cache.
  EventBus.$emit("refresh-invoice");
}

async function removeSchedule() {
  if (!props.invoice.job_id) return;
  const hasPayments =
    (props.invoice.progress_billing?.paid_to_date_cents ?? 0) > 0;
  if (hasPayments) {
    try {
      await store.dispatch("confirmDelete", {
        title: "Remove payment schedule?",
        className: "remove-schedule-confirm",
        message:
          '<p class="remove-schedule-body">This will <strong>permanently remove</strong> the payment schedule from this invoice. Existing payments will not be affected.</p>' +
          '<p class="remove-schedule-footnote">If you add a new schedule later, it will be sized to the remaining balance at that time.</p>',
        confirmInputValue: "I understand",
        hidePlaceholder: true
      });
    } catch {
      return;
    }
  }
  try {
    await store.dispatch("deleteJobPaymentSchedule", props.invoice.job_id);
    EventBus.$emit("refresh-invoice");
    window.notify?.success?.("Payment schedule removed.");
  } catch {
    // store action surfaces its own error toast
  }
}

function handleToggleMembershipSavings() {
  store
    .dispatch("updateInvoiceDisplayMembershipSavings", {
      url: props.invoice.links?.update_display_membership_savings,
      display_membership_savings: displayMembershipSavings.value
    })
    .then(() => {
      window.notify.success(
        "Successfully updated membership savings configuration."
      );
    });
}
</script>
